import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, Pencil } from "lucide-react";
import { useBreadcrumb } from "@/contexts/BreadcrumbContext";

interface Ticket {
  _id: string;
  name: string;
  email: string;
  quantity: number;
  status: string;
  event?: {
    _id: string;
    name: string;
  };
}

interface Event {
  _id: string;
  name: string;
}

export function Tickets() {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [events, setEvents] = useState<Event[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [ticketName, setTicketName] = useState("");
  const [ticketEmail, setTicketEmail] = useState("");
  const [ticketEvent, setTicketEvent] = useState("");
  const [ticketQuantity, setTicketQuantity] = useState("1");
  const [ticketStatus, setTicketStatus] = useState("complete");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { setBreadcrumbs, clearBreadcrumbs } = useBreadcrumb();

  useEffect(() => {
    async function fetchData() {
      try {
        const [ticketsResponse, eventsResponse] = await Promise.all([
          fetch(`/api/tickets`),
          fetch(`/api/events`),
        ]);

        if (ticketsResponse.ok) {
          const ticketsData = await ticketsResponse.json();
          setTickets(ticketsData.tickets || []);
        } else {
          console.error("Failed to fetch tickets");
        }

        if (eventsResponse.ok) {
          const eventsData = await eventsResponse.json();
          setEvents(eventsData.events || []);
        } else {
          console.error("Failed to fetch events");
        }

        setBreadcrumbs([
          { label: "Tickets", path: "/tickets" },
        ]);
      } catch (error) {
        console.error("Failed to fetch data:", error);
      } finally {
        setIsLoading(false);
      }
    }

    fetchData();

    // Cleanup: clear breadcrumbs when component unmounts
    return () => {
      clearBreadcrumbs();
    };
  }, []);

  async function handleCreateTicket() {
    if (!ticketName.trim() || !ticketEmail.trim() || !ticketEvent || !ticketQuantity || !ticketStatus) {
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await fetch(`/api/tickets`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: ticketName.trim(),
          email: ticketEmail.trim(),
          event: ticketEvent,
          quantity: parseInt(ticketQuantity, 10),
          status: ticketStatus,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        // Refresh tickets data to include the new ticket
        const ticketsResponse = await fetch(`/api/tickets`);
        if (ticketsResponse.ok) {
          const ticketsData = await ticketsResponse.json();
          setTickets(ticketsData.tickets || []);
        }
        setIsDrawerOpen(false);
        setTicketName("");
        setTicketEmail("");
        setTicketEvent("");
        setTicketQuantity("1");
        setTicketStatus("complete");
      } else {
        console.error("Failed to create ticket");
      }
    } catch (error) {
      console.error("Failed to create ticket:", error);
    } finally {
      setIsSubmitting(false);
    }
  }

  if (isLoading) {
    return <div className="p-4">Loading...</div>;
  }

  return (
    <div className="p-4">
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-2xl font-semibold">Tickets</h1>
        <Button onClick={() => setIsDrawerOpen(true)}>
          <Plus className="h-4 w-4" />
          Add Ticket
        </Button>
      </div>

      <Drawer open={isDrawerOpen} onOpenChange={setIsDrawerOpen}>
        <DrawerContent className="max-w-2xl mx-auto">
          <DrawerHeader>
            <DrawerTitle>Create Ticket</DrawerTitle>
          </DrawerHeader>
          <div className="p-4 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="ticket-name">Name</Label>
              <Input
                id="ticket-name"
                value={ticketName}
                onChange={(e) => setTicketName(e.target.value)}
                autoFocus
                placeholder="Enter ticket holder name"
                disabled={isSubmitting}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="ticket-email">Email</Label>
              <Input
                id="ticket-email"
                type="email"
                value={ticketEmail}
                onChange={(e) => setTicketEmail(e.target.value)}
                placeholder="Enter email address"
                disabled={isSubmitting}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="ticket-event">Event</Label>
              <Select value={ticketEvent} onValueChange={setTicketEvent} disabled={isSubmitting}>
                <SelectTrigger id="ticket-event">
                  <SelectValue placeholder="Select an event" />
                </SelectTrigger>
                <SelectContent>
                  {events.map((event) => (
                    <SelectItem key={event._id} value={event._id}>
                      {event.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="ticket-quantity">Quantity</Label>
              <Input
                id="ticket-quantity"
                type="number"
                min="1"
                value={ticketQuantity}
                onChange={(e) => setTicketQuantity(e.target.value)}
                placeholder="Enter quantity"
                disabled={isSubmitting}
              />
            </div>
          </div>
          <DrawerFooter className="flex-row gap-2 justify-center">
            <DrawerClose asChild>
              <Button variant="outline" disabled={isSubmitting}>
                Cancel
              </Button>
            </DrawerClose>
            <Button
              onClick={handleCreateTicket}
              disabled={
                !ticketName.trim() ||
                !ticketEmail.trim() ||
                !ticketEvent ||
                !ticketQuantity ||
                isSubmitting
              }
            >
              {isSubmitting ? "Creating..." : "Create"}
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Email</TableHead>
            <TableHead>Event</TableHead>
            <TableHead>Quantity</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="w-[100px]">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {tickets && tickets.length > 0 ? (
            tickets.map((ticket) => (
              <TableRow key={ticket._id}>
                <TableCell>{ticket.name || "-"}</TableCell>
                <TableCell>{ticket.email}</TableCell>
                <TableCell>
                  {ticket.event ? (
                    <Link
                      to={`/events/${ticket.event._id}/edit`}
                      className="text-blue-600 hover:text-blue-800 hover:underline"
                    >
                      {ticket.event.name}
                    </Link>
                  ) : (
                    "-"
                  )}
                </TableCell>
                <TableCell>{ticket.quantity}</TableCell>
                <TableCell>{ticket.status}</TableCell>
                <TableCell>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => navigate(`/tickets/${ticket._id}/edit`)}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={6} className="h-24 text-center">
                No tickets found.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}

